/**
 * sendAckWatchdog.ts — Round J durability invariant (2026-07-14).
 *
 * A dispatched turn must produce SOME acknowledgment within a bounded window:
 * a streaming message started in the originating thread, an assistant/system
 * message appended there, a discovered server task, or a genuine terminal
 * release of the thread's processing flag. When none of those happen (dead
 * handler path, silent `handled=true` with zero output, wedged VM turn that
 * never reaches the client) the watchdog fires so the caller can release the
 * thread's processing flag and append a visible, retryable error bubble —
 * silence is never an acceptable outcome for a send.
 *
 * Pure/injectable by design: no React, no globals beyond the default
 * setTimeout/clearTimeout (both overridable), so the timeout contract is
 * unit-testable with a synthetic clock.
 */

export const DEFAULT_SEND_ACK_TIMEOUT_MS = 25_000;

/**
 * Round J.1 (adversarial-review Finding 1, 2026-07-15): after a fire, the
 * turn the watchdog gave up on may STILL be alive (slow pre-dispatch gate,
 * degraded network) and produce a genuine ack later. For this window after a
 * fire, a turn-scoped ack is treated as a LATE ACK: the caller strips the
 * now-stale retry bubble (so Retry can't double-dispatch the turn) and the
 * thread's queued sends are held until a genuine terminal signal. Matches
 * the 10-minute processing safety floor.
 */
export const RECENT_FIRE_WINDOW_MS = 10 * 60 * 1000;

/** Map key for sends on surfaces without a thread switcher (threadId null). */
const SINGLE_THREAD_KEY = '__single_thread__';

export interface SendAckEntry {
  /** The original user text — retry resends exactly this through the safe path. */
  text: string;
  /** deps.now() reading at arm time. */
  armedAt: number;
}

export interface SendAckWatchdogDeps {
  /** Fired at most once per armed send when no ack arrived in time. */
  onTimeout: (threadId: string | null, entry: SendAckEntry) => void;
  /**
   * Round J.1: invoked at most once per fire when a turn-scoped ack arrives
   * for a thread whose watchdog FIRED within `recentFireWindowMs` — the turn
   * the fire gave up on turned out to be alive. The caller strips the stale
   * send_failure_retry bubble so the user can't Retry into a duplicate turn.
   * Acks made synchronously from inside the onTimeout handler itself (its own
   * stopProcessing / failure-bubble append) are NOT late acks and never
   * trigger this.
   */
  onLateAck?: (threadId: string | null, entry: SendAckEntry) => void;
  timeoutMs?: number;
  /** Round J.1: how long after a fire a turn-scoped ack counts as late. */
  recentFireWindowMs?: number;
  setTimeoutFn?: (cb: () => void, ms: number) => unknown;
  clearTimeoutFn?: (handle: unknown) => void;
  now?: () => number;
}

export interface SendAckWatchdog {
  /**
   * Arm (or re-arm, resetting the clock) the watchdog for a thread's dispatch.
   *
   * `timeoutMs` (adversarial review 2026-07-15, Finding 3) optionally
   * overrides the watchdog's default window FOR THIS ARM ONLY: a dispatch
   * whose legitimate turn outlives the default (e.g. the guest chat turn's
   * 45s server model budget vs. the 25s default) must arm with a longer
   * window, otherwise the watchdog fires mid-turn and offers a tappable
   * Retry while the original billed call is still running — a double-spend
   * path. Omitted → the constructor's timeoutMs (default
   * DEFAULT_SEND_ACK_TIMEOUT_MS).
   */
  arm(threadId: string | null, text: string, timeoutMs?: number, turnKey?: string): void;
  /** Acknowledge activity on a thread; returns true if an armed entry was cancelled. */
  ack(threadId: string | null): boolean;
  isArmed(threadId: string | null): boolean;
  /**
   * Round J.1 queue-drain hold: true while this thread's watchdog fired
   * recently and NO genuine post-fire signal (late ack / terminal release)
   * has arrived. While true the caller must NOT drain queued sends for the
   * thread — the original turn may still be live and dispatching a queued
   * send would run two concurrent turns on one thread.
   */
  isAwaitingLateAck(threadId: string | null): boolean;
  /** Milliseconds until the late-ack hold expires on its own (0 when none). */
  lateAckHoldRemainingMs(threadId: string | null): number;
  /**
   * Cancel a single thread's armed entry AND its recent-fire record without
   * firing (thread delete/archive — Finding 8c).
   */
  cancelThread(threadId: string | null): void;
  /** Cancel every armed entry without firing (unmount teardown). */
  cancelAll(): void;
}

export function createSendAckWatchdog(deps: SendAckWatchdogDeps): SendAckWatchdog {
  const timeoutMs = deps.timeoutMs ?? DEFAULT_SEND_ACK_TIMEOUT_MS;
  const recentFireWindowMs = deps.recentFireWindowMs ?? RECENT_FIRE_WINDOW_MS;
  const setTimeoutFn = deps.setTimeoutFn ?? ((cb: () => void, ms: number) => setTimeout(cb, ms));
  const clearTimeoutFn = deps.clearTimeoutFn
    ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const now = deps.now ?? (() => Date.now());

  const armed = new Map<string, { threadId: string | null; entry: SendAckEntry; handle: unknown }>();
  // Round J.1: threads whose watchdog fired recently and are awaiting a
  // possible late ack from the still-live turn.
  const fired = new Map<string, { threadId: string | null; entry: SendAckEntry; firedAt: number }>();
  // Keys whose onTimeout handler is executing RIGHT NOW. The handler's own
  // stopProcessing/appendSendFailureBubble re-enter ack() synchronously —
  // those must never count as late acks for the fire they belong to.
  const firingKeys = new Set<string>();
  const keyFor = (threadId: string | null): string => threadId ?? SINGLE_THREAD_KEY;

  const cancelKey = (key: string): boolean => {
    const existing = armed.get(key);
    if (!existing) return false;
    armed.delete(key);
    clearTimeoutFn(existing.handle);
    return true;
  };

  /** Fresh (non-expired) fire record for the key, lazily expiring stale ones. */
  const freshFireRecord = (key: string) => {
    const record = fired.get(key);
    if (!record) return null;
    if (now() - record.firedAt > recentFireWindowMs) {
      fired.delete(key);
      return null;
    }
    return record;
  };

  return {
    arm(threadId: string | null, text: string, armTimeoutMs?: number, turnKey?: string): void {
      // In-thread parallelism slice 1 (2026-08-19): a dispatch that carries
      // its own turnKey arms a PER-TURN entry — a second concurrent turn's
      // arm must never cancel the first turn's window (silence was the
      // failure mode: turn A dies, nothing fires). Key-less arms keep the
      // exact legacy semantics: one entry per thread, re-arm supersedes.
      const key = turnKey ? `${keyFor(threadId)}::${turnKey}` : keyFor(threadId);
      // Re-arm supersedes THIS key only: same turn (or the legacy
      // thread-level slot) owns its window; sibling turns are untouched.
      cancelKey(key);
      // Finding 3: per-arm override for dispatches whose legitimate turn
      // outlives the default window (guest chat's 45s model budget).
      const windowMs =
        typeof armTimeoutMs === 'number' && Number.isFinite(armTimeoutMs) && armTimeoutMs > 0
          ? armTimeoutMs
          : timeoutMs;
      const entry: SendAckEntry = { text, armedAt: now() };
      const handle = setTimeoutFn(() => {
        // Delete BEFORE notifying so the timeout fires exactly once even if
        // onTimeout itself acks/arms or throws.
        armed.delete(key);
        // Record the fire BEFORE the handler runs, but shield the handler's
        // own synchronous acks from consuming it as a late ack.
        fired.set(key, { threadId, entry, firedAt: now() });
        firingKeys.add(key);
        try {
          deps.onTimeout(threadId, entry);
        } finally {
          firingKeys.delete(key);
        }
      }, windowMs);
      armed.set(key, { threadId, entry, handle });
    },

    ack(threadId: string | null): boolean {
      const key = keyFor(threadId);
      // Cancel EVERY armed entry for this thread (legacy slot + per-turn
      // slots). Thread-scoped acks cannot attribute activity to one turn;
      // cancelling all matches the legacy one-entry semantics and errs
      // toward silence-avoidance's cheaper failure (a missed fire is
      // backstopped by the processing safety floor; a false fire mid-turn
      // offers a double-dispatch Retry).
      let cancelled = cancelKey(key);
      for (const [k, v] of Array.from(armed.entries())) {
        if (k !== key && keyFor(v.threadId) === key) {
          cancelled = cancelKey(k) || cancelled;
        }
      }
      // Round J.1: a turn-scoped ack on a thread that FIRED recently is a
      // late ack — the "dead" turn produced real output/terminal after all.
      // Consume the fire record (once) and notify so the caller strips the
      // stale retry bubble. Suppressed while the fire's own onTimeout
      // handler is running (its release/bubble re-enter ack()).
      if (!firingKeys.has(key)) {
        const record = freshFireRecord(key);
        if (record) {
          fired.delete(key);
          try {
            deps.onLateAck?.(record.threadId, record.entry);
          } catch { /* late-ack reconciliation is best-effort */ }
        }
      }
      return cancelled;
    },

    isArmed(threadId: string | null): boolean {
      const key = keyFor(threadId);
      if (armed.has(key)) return true;
      for (const v of armed.values()) {
        if (keyFor(v.threadId) === key) return true;
      }
      return false;
    },

    isAwaitingLateAck(threadId: string | null): boolean {
      return freshFireRecord(keyFor(threadId)) !== null;
    },

    lateAckHoldRemainingMs(threadId: string | null): number {
      const record = freshFireRecord(keyFor(threadId));
      if (!record) return 0;
      return Math.max(0, recentFireWindowMs - (now() - record.firedAt));
    },

    cancelThread(threadId: string | null): void {
      const key = keyFor(threadId);
      cancelKey(key);
      for (const [k, v] of Array.from(armed.entries())) {
        if (keyFor(v.threadId) === key) cancelKey(k);
      }
      fired.delete(key);
    },

    cancelAll(): void {
      for (const key of [...armed.keys()]) cancelKey(key);
      fired.clear();
    },
  };
}
